// @ts-nocheck

import dayjs from "dayjs";
import { Settings, X, ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../../utils/api";

// Zoom configuration constants
const ZOOM_CONFIG = {
  MIN: 0.01,
  MAX: 3,
  BUTTON_IN_FACTOR: 0.2,
  BUTTON_OUT_FACTOR: -0.2,
  WHEEL_SENSITIVITY: 0.2,
  WHEEL_PIXEL_MODE_MULTIPLIER: 0.01,
  WHEEL_LINE_MODE_MULTIPLIER: 0.05,
  WHEEL_MAX_DELTA: 1,
};

const STATUSES = {
  connected: "connected",
  not_connected: "not_connected",
};

interface IProgressReportViewer {
  sectionData: any;
  projectId: string;
  lvItemId: string;
  lvName: string;
  lvItemPosition: string;
  onClose?: () => void;
}

const ConnectPlanToLV = ({
  sectionData,
  projectId,
  lvItemId,
  lvName,
  lvItemPosition,
  onClose,
}: IProgressReportViewer) => {
  const { t } = useTranslation();
  const [detectedObjects, setDetectedObjects] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [planImageLoaded, setPlanImageLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isHoveringObject, setIsHoveringObject] = useState(false);
  const dragStartCoords = useRef({ x: 0, y: 0 });

  // Mobile touch states
  const [isTouching, setIsTouching] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialZoom, setInitialZoom] = useState(1);
  const touchStartCoords = useRef({ x: 0, y: 0 });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const planImageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedObject = useMemo(() => {
    return selectedObjects[selectedObjects.length - 1];
  }, [selectedObjects]);

  const filteredObjects = useMemo(() => {
    return detectedObjects
      .filter((obj) => selectedStatuses.includes(obj.status))
      .filter((obj) => selectedTypes.includes(obj.group_id));
  }, [detectedObjects, selectedStatuses, selectedTypes]);

  useEffect(() => {
    const setZoomToFit = () => {
      const container = containerRef.current;
      const img = planImageRef?.current;
      if (container && img) {
        const containerRect = container.getBoundingClientRect();
        const imgNaturalWidth = img.naturalWidth;
        const imgNaturalHeight = img.naturalHeight;
        if (imgNaturalWidth && imgNaturalHeight) {
          const scaleX = containerRect.width / imgNaturalWidth;
          const scaleY = containerRect.height / imgNaturalHeight;
          const fitZoom = Math.min(scaleX, scaleY, 1);
          setZoomLevel(fitZoom);
          setPanOffset({ x: 0, y: 0 });
        }
      }
    };

    const imageElement = planImageRef.current;
    if (imageElement && imageElement.complete) {
      setZoomToFit();
    } else if (imageElement) {
      imageElement.onload = setZoomToFit;
    }
    return () => {
      if (imageElement) {
        imageElement.onload = null;
      }
    };
  }, [containerRef]);

  // Size canvas to container (not image) to avoid huge canvas allocations
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();

    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(container);

    return () => observer.disconnect();
  }, [planImageLoaded]);

  const drawObjectsOnCanvas = useCallback(
    ({ visible = true, objects = [], isFlashMode = false }) => {
      const canvas = canvasRef.current;
      const planImage = planImageRef.current;

      if (!canvas || !planImage) return;

      const ctx = canvas.getContext("2d");

      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!visible) {
        return;
      }

      // Apply DPR scaling, then pan/zoom to map image-space coords to container-space
      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.translate(panOffset.x, panOffset.y);
      ctx.scale(zoomLevel, zoomLevel);

      objects.forEach((obj) => {
        const x = obj.position.x;
        const y = obj.position.y;
        const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
        const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;

        let color = "green";
        if (obj.status === "not_connected") {
          color = "red";
        }

        const isSelected = selectedObjects.some(
          (object) => object._id === obj._id,
        );

        ctx.beginPath();
        ctx.arc(x, y, Math.max(width, height) / 2, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.globalAlpha = isFlashMode || zoomLevel < 0.4 ? 1 : 0.2;
        ctx.fill();
        ctx.globalAlpha = 1;

        // Draw border if selected
        if (isSelected) {
          ctx.lineWidth = 3 / zoomLevel;
          ctx.strokeStyle = "#000000"; // black border for selection
          ctx.setLineDash([5, 5]);
          ctx.stroke();
          ctx.setLineDash([]);
        } else if (!(isFlashMode || zoomLevel < 0.4)) {
          ctx.lineWidth = 1;
          ctx.strokeStyle = color; // black border for selection
          ctx.setLineDash([]); // solid border
          ctx.stroke();
        }
      });

      ctx.restore();
    },
    [zoomLevel, panOffset, selectedObjects],
  );

  useEffect(() => {
    // Load detected objects for this section
    const loadDetectedObjects = async () => {
      if (!sectionData?._id || !projectId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.fetchDetectedObjectsBySection(
          projectId,
          sectionData._id,
        );

        if (response.success) {
          const normalizedData = response.data.map((obj: any) => ({
            ...obj,
            status: obj.lvItemId ? "connected" : "not_connected",
          }));
          setDetectedObjects(normalizedData || []);
          console.log(
            `Loaded ${response.data?.length || 0} detected objects for section ${sectionData._id}`,
          );
        } else {
          throw new Error(
            response.message || "Failed to fetch detected objects",
          );
        }
      } catch (error) {
        console.error("Error loading detected objects:", error);
        setError(error.message || "Failed to load detected objects");
        setDetectedObjects([]);
      } finally {
        setLoading(false);
      }
    };

    loadDetectedObjects();
  }, [sectionData?._id, projectId]);

  useEffect(() => {
    if (planImageLoaded && detectedObjects.length > 0) {
      const filteredObjects = detectedObjects
        .filter((obj) => selectedStatuses.includes(obj.status))
        .filter((obj) => selectedTypes.includes(obj.group_id.toString()));

      drawObjectsOnCanvas({ visible: true, objects: filteredObjects });
    }
  }, [
    detectedObjects,
    planImageLoaded,
    selectedStatuses,
    selectedTypes,
    drawObjectsOnCanvas,
  ]);

  useEffect(() => {
    if (planImageLoaded && detectedObjects.length > 0) {
      drawObjectsOnCanvas({
        visible: true,
        objects: detectedObjects,
        isFlashMode: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedObjects, planImageLoaded]);

  const handleObjectClick = useCallback(
    (event) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      let clickX = event.clientX - containerRect.left;
      let clickY = event.clientY - containerRect.top;

      // Adjust click coordinates for canvas transforms (pan and zoom)
      clickX = (clickX - panOffset.x) / zoomLevel;
      clickY = (clickY - panOffset.y) / zoomLevel;

      // Find clicked object
      const clickedObject = detectedObjects.find((obj) => {
        const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
        const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;

        const x = obj.position.x - width / 2;
        const y = obj.position.y - height / 2;

        const isInBounds =
          clickX >= x &&
          clickX <= x + width &&
          clickY >= y &&
          clickY <= y + height;

        return isInBounds;
      });

      if (clickedObject) {
        setSelectedObjects([clickedObject]);
      } else {
        setSelectedObjects([]);
      }
    },
    [panOffset.x, panOffset.y, zoomLevel, detectedObjects],
  );

  const handleZoom = useCallback(
    (directionOrEvent, mousePosition = null) => {
      let delta;
      let zoomPoint = mousePosition;

      if (typeof directionOrEvent === "string") {
        delta =
          directionOrEvent === "in"
            ? ZOOM_CONFIG.BUTTON_IN_FACTOR
            : ZOOM_CONFIG.BUTTON_OUT_FACTOR;
      } else if (directionOrEvent && directionOrEvent.deltaY !== undefined) {
        const event = directionOrEvent;
        delta = event.deltaY;

        if (event.deltaMode === 0) {
          delta *= ZOOM_CONFIG.WHEEL_PIXEL_MODE_MULTIPLIER;
        } else if (event.deltaMode === 1) {
          delta *= ZOOM_CONFIG.WHEEL_LINE_MODE_MULTIPLIER;
        }

        delta = Math.max(
          -ZOOM_CONFIG.WHEEL_MAX_DELTA,
          Math.min(ZOOM_CONFIG.WHEEL_MAX_DELTA, delta),
        );

        delta = -delta;

        if (!zoomPoint) {
          zoomPoint = {
            x: event.clientX,
            y: event.clientY,
          };
        }
      } else {
        return;
      }

      const factor = Math.exp(delta * ZOOM_CONFIG.WHEEL_SENSITIVITY);
      const newZoom = zoomLevel * factor;
      const clampedZoom = Math.max(
        ZOOM_CONFIG.MIN,
        Math.min(ZOOM_CONFIG.MAX, newZoom),
      );

      const containerRect = containerRef.current.getBoundingClientRect();
      const mx = zoomPoint
        ? zoomPoint.x - containerRect.left
        : containerRect.width / 2;
      const my = zoomPoint
        ? zoomPoint.y - containerRect.top
        : containerRect.height / 2;

      const imageX = (mx - panOffset.x) / zoomLevel;
      const imageY = (my - panOffset.y) / zoomLevel;

      const newPanX = mx - imageX * clampedZoom;
      const newPanY = my - imageY * clampedZoom;

      setZoomLevel(clampedZoom);
      setPanOffset({ x: newPanX, y: newPanY });
    },
    [zoomLevel, panOffset],
  );

  const handleContainerMouseMove = (event) => {
    if (isDragging) {
      return;
    }

    if (!planImageLoaded) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    let moveX = event.clientX - containerRect.left;
    let moveY = event.clientY - containerRect.top;

    moveX = (moveX - panOffset.x) / zoomLevel;
    moveY = (moveY - panOffset.y) / zoomLevel;

    const isOverObject = detectedObjects.some((obj) => {
      const x = obj.position.x;
      const y = obj.position.y;
      const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 3) : 40;
      const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 3) : 40;

      return (
        moveX >= x && moveX <= x + width && moveY >= y && moveY <= y + height
      );
    });

    if (isOverObject !== isHoveringObject) {
      setIsHoveringObject(isOverObject);
    }
  };

  const handleMouseDown = (event) => {
    // Handle both mouse clicks and trackpad clicks
    if (event.button === 0) {
      // Left mouse button or trackpad click
      setIsDragging(true);
      setDragStart({
        x: event.clientX - panOffset.x,
        y: event.clientY - panOffset.y,
      });
      dragStartCoords.current = { x: event.clientX, y: event.clientY };
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleMouseMove = useCallback(
    (event) => {
      if (isDragging) {
        const newPanOffset = {
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        };
        setPanOffset(newPanOffset);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(
    (event) => {
      if (isDragging) {
        setIsDragging(false);

        const dx = Math.abs(event.clientX - dragStartCoords.current.x);
        const dy = Math.abs(event.clientY - dragStartCoords.current.y);

        if (dx < 5 && dy < 5) {
          handleObjectClick(event);
        }

        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, handleObjectClick],
  );

  const handleMouseLeave = () => {
    // Stop dragging if mouse leaves the container
    setIsDragging(false);
  };

  // Mobile touch event handlers
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touches) => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    const x = (touches[0].clientX + touches[1].clientX) / 2;
    const y = (touches[0].clientY + touches[1].clientY) / 2;
    return { x, y };
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    if (event.touches.length === 1) {
      // Single finger - start panning
      setIsTouching(true);
      const touch = event.touches[0];
      setTouchStart({
        x: touch.clientX - panOffset.x,
        y: touch.clientY - panOffset.y,
      });
      touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
    } else if (event.touches.length === 2) {
      // Two fingers - start pinch zoom
      setIsTouching(false); // Stop panning
      const distance = getTouchDistance(event.touches);
      setInitialPinchDistance(distance);
      setInitialZoom(zoomLevel);
    }
  };

  const handleTouchMove = useCallback(
    (event) => {
      event.preventDefault();
      if (event.touches.length === 1 && isTouching) {
        // Single finger - pan
        const touch = event.touches[0];
        const newPanOffset = {
          x: touch.clientX - touchStart.x,
          y: touch.clientY - touchStart.y,
        };
        setPanOffset(newPanOffset);
      } else if (event.touches.length === 2) {
        // Two fingers - pinch zoom
        const distance = getTouchDistance(event.touches);
        const center = getTouchCenter(event.touches);

        if (initialPinchDistance > 0) {
          const scale = distance / initialPinchDistance;
          const newZoom = Math.max(0.1, Math.min(5, initialZoom * scale));

          // Calculate zoom with center point
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            const mousePosition = {
              x: center.x - containerRect.left,
              y: center.y - containerRect.top,
            };

            // Apply zoom centered on pinch point
            const zoomFactor = newZoom / zoomLevel;
            const newPanX =
              mousePosition.x - (mousePosition.x - panOffset.x) * zoomFactor;
            const newPanY =
              mousePosition.y - (mousePosition.y - panOffset.y) * zoomFactor;

            setZoomLevel(newZoom);
            setPanOffset({ x: newPanX, y: newPanY });
          }
        }
      }
    },
    [
      isTouching,
      touchStart,
      initialPinchDistance,
      initialZoom,
      zoomLevel,
      panOffset,
    ],
  );

  const handleTouchEnd = useCallback(
    (event) => {
      if (event.touches.length === 0) {
        // All fingers lifted
        if (isTouching) {
          setIsTouching(false);

          // Check if it was a tap (minimal movement)
          const touch = event.changedTouches[0];
          const dx = Math.abs(touch.clientX - touchStartCoords.current.x);
          const dy = Math.abs(touch.clientY - touchStartCoords.current.y);

          if (dx < 10 && dy < 10) {
            // Simulate click for object selection
            const syntheticEvent = {
              clientX: touch.clientX,
              clientY: touch.clientY,
              preventDefault: () => {},
              stopPropagation: () => {},
            };
            handleObjectClick(syntheticEvent);
          }
        }

        // Reset pinch zoom state
        setInitialPinchDistance(0);
        setInitialZoom(1);
      } else if (event.touches.length === 1) {
        // One finger remaining, restart single touch
        setInitialPinchDistance(0);
        if (!isTouching) {
          const touch = event.touches[0];
          setIsTouching(true);
          setTouchStart({
            x: touch.clientX - panOffset.x,
            y: touch.clientY - panOffset.y,
          });
          touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
        }
      }
    },
    [isTouching, handleObjectClick, panOffset],
  );

  // Handle wheel zoom
  const handleWheel = useCallback(
    (event) => {
      event.preventDefault();
      handleZoom(event);
    },
    [handleZoom],
  );

  // Add wheel event listener to container
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });

      return () => {
        container.removeEventListener("wheel", handleWheel);
      };
    }
  }, [handleWheel]);

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStart]);

  // Add global touch event listeners for touch interactions
  useEffect(() => {
    if (isTouching) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: false });

      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTouching, touchStart]);

  const getObjectStats = () => {
    const total = detectedObjects.length;
    const connected = detectedObjects.filter(
      (obj) => obj.status === "connected",
    ).length;
    const notConnected = detectedObjects.filter(
      (obj) => obj.status === "not_connected",
    ).length;

    return {
      total,
      connected,
      notConnected,
    };
  };

  const stats = getObjectStats();

  const handleStatusChange = (status) => {
    let statuses;
    if (status === null) {
      statuses =
        selectedStatuses.length === Object.keys(STATUSES).length
          ? []
          : Object.keys(STATUSES);
    } else {
      statuses = selectedStatuses.includes(status)
        ? selectedStatuses.filter((s) => s !== status)
        : [...selectedStatuses, status];
    }
    setSelectedStatuses(statuses);
    setSelectedTypes([]);
  };

  const groupedObjects = useMemo(() => {
    return detectedObjects
      .filter((obj) => selectedStatuses.includes(obj.status))
      .reduce((acc, obj) => {
        if (!acc[obj.group_id]) acc[obj.group_id] = [];
        acc[obj.group_id].push(obj);
        return acc;
      }, {});
  }, [detectedObjects, selectedStatuses]);

  useEffect(() => {
    setSelectedTypes(
      Object.keys(groupedObjects).map((dwgLayer) => dwgLayer.toString()),
    );
  }, [groupedObjects]);

  useEffect(() => {
    setSelectedStatuses(Object.keys(STATUSES));
  }, [detectedObjects]);

  const handleTypeChange = (type) => {
    let types;
    if (type === null) {
      types =
        selectedTypes.length === Object.keys(groupedObjects).length
          ? []
          : Object.keys(groupedObjects);
    } else {
      types = selectedTypes.includes(type)
        ? selectedTypes.filter((t) => t !== type)
        : [...selectedTypes, type];
    }
    setSelectedTypes(types);
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b bg-gray-50">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t("progress_report.title", { sectionName: sectionData.name })}
              </h3>
              <h4 className="text-sm text-gray-500">
                {/* TODO: Remove this , keep only position, after migration */}
                LV Position: {lvItemPosition || lvItemId}
              </h4>
              <h4 className="text-sm text-gray-500">LV Name: {lvName}</h4>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleZoom("out")}
                className="p-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50"
                title={t("progress_report.zoom_out")}
                disabled={zoomLevel <= 0.01}
              >
                <ZoomOut size={16} />
              </button>

              <button
                onClick={() => handleZoom("in")}
                className="p-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50"
                title={t("progress_report.zoom_in")}
                disabled={zoomLevel >= 3}
              >
                <ZoomIn size={16} />
              </button>

              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700"
                title={t("progress_report.close")}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Plan View */}
            <div
              className="flex-1 relative bg-gray-100 overflow-hidden"
              ref={containerRef}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleContainerMouseMove}
              style={{
                cursor: isHoveringObject
                  ? "pointer"
                  : isDragging || isTouching
                    ? "grabbing"
                    : "grab",
                touchAction: "none", // Prevent default touch behaviors
              }}
            >
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-600">
                      {t("progress_report.loading_objects")}
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="absolute inset-0 flex items-center justify-center text-red-500 z-10">
                  <p>{t("progress_report.loading_error", { error })}</p>
                </div>
              )}

              {/* Plan Image */}
              <div
                className="relative w-full h-full"
                style={{ overflow: "hidden" }}
              >
                {sectionData.plan?.previewUrl ? (
                  <div
                    className="relative w-full h-full"
                    style={{
                      transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                      transformOrigin: "top left",
                      willChange: "transform",
                    }}
                  >
                    <img
                      ref={planImageRef}
                      src={sectionData.plan.previewUrl}
                      alt={t("progress_report.plan_alt", {
                        sectionName: sectionData.name,
                      })}
                      className="max-w-none select-none"
                      onLoad={() => setPlanImageLoaded(true)}
                      onError={() =>
                        setError(t("progress_report.image_load_error"))
                      }
                      draggable={false}
                    />

                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <Settings size={48} className="mx-auto mb-2" />
                      <p>{t("progress_report.no_plan_available")}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Object Overlay Canvas - sized to container, not image */}
              <canvas
                ref={canvasRef}
                className="absolute top-0 left-0 pointer-events-none"
              />
            </div>

            <div className="w-80 border-l bg-white flex flex-col">
              {/* Object Details */}
              {selectedObject ? (
                <div className="p-4 border-b">
                  <h4 className="font-medium text-gray-900 mb-2">
                    {t("progress_report.object_details")}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">
                        {t("progress_report.type")}:
                      </span>{" "}
                      {selectedObject.type}
                    </div>
                    <div>
                      <span className="font-medium">
                        {t("progress_report.group")}:
                      </span>{" "}
                      {selectedObject.group_id}
                    </div>
                    <div>
                      <span className="font-medium">
                        {t("progress_report.status")}:
                      </span>
                      <span
                        className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                          selectedObject.status === "connected"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {selectedObject.status}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">
                        {t("progress_report.position")}:
                      </span>{" "}
                      ({selectedObject.position.x}, {selectedObject.position.y})
                    </div>
                    <div>
                      <span className="font-medium">
                        {t("progress_report.detection_date")}:
                      </span>{" "}
                      {dayjs
                        .utc(selectedObject.detectionDate)
                        .format("YYYY-MM-DD HH:mm")}
                    </div>
                    <div>
                      <span className="font-medium">
                        {t("progress_report.description")}:
                      </span>{" "}
                      {selectedObject.description}
                    </div>

                    <button
                      className="w-full px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center"
                      onClick={async () => {
                        try {
                          await api.connectDetectedObjectsToLvByLayer(
                            projectId,
                            lvItemId,
                            selectedObject.group_id,
                            sectionData.plan.id,
                          );
                          toast.success(t("progress_report.connected_to_lv"));
                        } catch (error) {
                          toast.error(error.message);
                        }
                      }}
                    >
                      {t("progress_report.connect_to_lv")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 border-b text-center text-gray-500">
                  <Settings size={24} className="mx-auto mb-2" />
                  <p className="text-sm">
                    {t("progress_report.click_object_for_details")}
                  </p>
                </div>
              )}

              {/* Objects List */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4">
                  <div className="flex flex-col items-center space-x-4 text-md text-gray-600 mt-1">
                    {[
                      {
                        key: null,
                        label: t("progress_report.total_objects"),
                        colorClass:
                          "text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm mb-2",
                        stat: stats.total,
                      },
                      {
                        key: STATUSES.connected,
                        label: t("progress_report.connected"),
                        colorClass:
                          "text-green-600 bg-green-50 hover:bg-green-100",
                        stat: stats.connected,
                      },
                      {
                        key: STATUSES.not_connected,
                        label: t("progress_report.not_connected"),
                        colorClass: "text-red-600 bg-red-50 hover:bg-red-100",
                        stat: stats.notConnected,
                      },
                    ].map(({ key, label, colorClass, stat }) => (
                      <label
                        key={label}
                        className={`flex items-center space-x-1 cursor-pointer select-none ${colorClass} px-2 py-1 rounded transition-colors w-full`}
                      >
                        <input
                          type="checkbox"
                          checked={
                            key === null
                              ? selectedStatuses.length ===
                                Object.keys(STATUSES).length
                              : selectedStatuses.includes(key)
                          }
                          onChange={() => handleStatusChange(key)}
                          className="form-checkbox accent-blue-600"
                        />
                        <span>
                          {label}: ({stat})
                        </span>
                      </label>
                    ))}
                  </div>
                  {Object.keys(groupedObjects).length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 mt-2">
                        {t("progress_report.detected_object_groups")}
                      </h4>
                      <div className="flex flex-col items-center space-x-4 text-sm text-gray-600 mt-1">
                        <label
                          className={`flex items-center space-x-1 cursor-pointer select-nonetext-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm mb-2 px-2 py-1 rounded transition-colors w-full`}
                        >
                          <input
                            type="checkbox"
                            checked={
                              selectedTypes.length ===
                              Object.keys(groupedObjects).length
                            }
                            onChange={() => handleTypeChange(null)}
                            className="form-checkbox accent-blue-600"
                          />
                          <span>
                            {t("progress_report.all_types")}: (
                            {Object.values(groupedObjects).reduce(
                              (sum, arr) => sum + arr.length,
                              0,
                            )}
                            )
                          </span>
                        </label>
                        {Object.keys(groupedObjects)
                          .map((type) => ({
                            key: type,
                            label: type,
                            stat: groupedObjects[type].length,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .map(({ key, label, stat }) => (
                            <label
                              key={label}
                              className={`flex items-center space-x-1 cursor-pointer select-none text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors w-full`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTypes.includes(key)}
                                onChange={() => handleTypeChange(key)}
                                className="form-checkbox accent-blue-600"
                              />
                              <span>
                                {label}: ({stat})
                              </span>
                            </label>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConnectPlanToLV;
